<template>
  <form class="location-form" @submit.prevent="onSubmit">
    <!-- Komunikat błędu (np. konflikt 409 przy usuwaniu pasa z pomiarami, walidacja) -->
    <div v-if="errorMessage" class="alert alert-danger" role="alert">
      {{ errorMessage }}
    </div>

    <h4>{{ t('location-data') }}</h4>

    <div class="row g-3">
      <div class="col-md-3">
        <label class="form-label">{{ t('location-id') }} *</label>
        <input
          type="number"
          class="form-control"
          v-model.number="form.id"
          :disabled="isEdit"
          min="1"
          required
        />
        <small class="text-muted">{{ t('location-id-hint') }}</small>
      </div>
      <div class="col-md-5">
        <label class="form-label">{{ t('location-name') }} *</label>
        <input type="text" class="form-control" v-model.trim="form.name" maxlength="255" required />
      </div>
      <div class="col-md-4">
        <label class="form-label">{{ t('location-short-name') }} *</label>
        <input type="text" class="form-control" v-model.trim="form.shortName" maxlength="20" required />
      </div>

      <div class="col-md-4">
        <label class="form-label">{{ t('city-name') }} *</label>
        <input type="text" class="form-control" v-model.trim="form.cityName" maxlength="255" required />
      </div>
      <div class="col-md-4">
        <label class="form-label">{{ t('road-number') }} *</label>
        <input type="text" class="form-control" v-model.trim="form.roadNumber" maxlength="255" required />
      </div>
      <div class="col-md-4">
        <label class="form-label">{{ t('organization') }} *</label>
        <input type="text" class="form-control" v-model.trim="form.organization" maxlength="255" required />
      </div>

      <div class="col-md-3">
        <label class="form-label">{{ t('chainage') }} *</label>
        <input type="text" class="form-control" v-model.trim="form.chainage" maxlength="255" required />
      </div>
      <div class="col-md-3">
        <label class="form-label">{{ t('direction') }} *</label>
        <select class="form-select" v-model="form.direction" required>
          <option value="L">{{ t('direction-l') }}</option>
          <option value="P">{{ t('direction-p') }}</option>
        </select>
      </div>
      <div class="col-md-3">
        <label class="form-label">{{ t('direction-from') }}</label>
        <input type="text" class="form-control" v-model.trim="form.directionFrom" maxlength="255" />
      </div>
      <div class="col-md-3">
        <label class="form-label">{{ t('direction-to') }}</label>
        <input type="text" class="form-control" v-model.trim="form.directionTo" maxlength="255" />
      </div>

      <div class="col-md-4">
        <label class="form-label">{{ t('geo-lat') }} *</label>
        <input type="text" class="form-control" v-model.trim="form.geoLat" maxlength="255" required />
      </div>
      <div class="col-md-4">
        <label class="form-label">{{ t('geo-long') }} *</label>
        <input type="text" class="form-control" v-model.trim="form.geoLong" maxlength="255" required />
      </div>
      <div class="col-md-4">
        <label class="form-label">{{ t('checkpoint-distance') }} *</label>
        <input type="number" class="form-control" v-model.number="form.checkpointDistance" min="0" required />
      </div>
    </div>

    <h4 class="mt-4">{{ t('location-data-retention') }}</h4>
    <div class="row g-3">
      <div class="col-md-4">
        <label class="form-label">{{ t('data-time-limit') }} *</label>
        <input type="number" class="form-control" v-model.number="form.dataTimeLimit" min="1" required />
      </div>
      <div class="col-md-4">
        <label class="form-label">{{ t('no-violation-data-time-limit') }}</label>
        <input type="number" class="form-control" v-model.number="form.noViolationDataTimeLimit" min="0" />
      </div>
      <div class="col-md-4">
        <label class="form-label">{{ t('images-life-limit') }}</label>
        <input type="number" class="form-control" v-model.number="form.imagesLifeLimit" min="0" />
      </div>
      <div class="col-md-8">
        <label class="form-label">{{ t('ftp-report-sub-path') }}</label>
        <input type="text" class="form-control" v-model.trim="form.ftpReportSubPath" maxlength="255" />
      </div>
    </div>
    <div class="checkbox-row">
      <div class="form-check">
        <input class="form-check-input" type="checkbox" id="deleteImages" v-model="form.deleteImages" />
        <label class="form-check-label" for="deleteImages">{{ t('delete-images') }}</label>
      </div>
    </div>

    <h4 class="mt-4">{{ t('location-gddkia') }}</h4>
    <div class="row g-3">
      <div class="col-md-4">
        <label class="form-label">{{ t('gddkia-id') }}</label>
        <input type="number" class="form-control" v-model.number="form.gddkiaId" min="0" />
      </div>
      <div class="col-md-4">
        <label class="form-label">{{ t('send-data-to-gddkia-interval') }} *</label>
        <select class="form-select" v-model.number="form.sendDataToGddkiaInterval" required>
          <option :value="60">60</option>
          <option :value="90">90</option>
          <option :value="120">120</option>
        </select>
      </div>
    </div>
    <div class="checkbox-row">
      <div class="form-check">
        <input class="form-check-input" type="checkbox" id="sendDataToGddkia" v-model="form.sendDataToGddkia" />
        <label class="form-check-label" for="sendDataToGddkia">{{ t('send-data-to-gddkia') }}</label>
      </div>
      <div class="form-check">
        <input class="form-check-input" type="checkbox" id="sendLogToGddkia" v-model="form.sendLogToGddkia" />
        <label class="form-check-label" for="sendLogToGddkia">{{ t('send-log-to-gddkia') }}</label>
      </div>
      <div class="form-check">
        <input class="form-check-input" type="checkbox" id="cameraPointEnabled" v-model="form.cameraPointEnabled" />
        <label class="form-check-label" for="cameraPointEnabled">{{ t('camera-point-enabled') }}</label>
      </div>
    </div>

    <!-- Sekcja widoczności: grupy ról, które mają widzieć tę stację preselekcji -->
    <h4 class="mt-4">{{ t('location-visibility') }}</h4>
    <p class="text-muted mb-2">{{ t('location-visibility-hint') }}</p>
    <div v-if="groupRoles.length" class="group-roles-grid">
      <div v-for="group in groupRoles" :key="group.id" class="form-check">
        <input
          class="form-check-input"
          type="checkbox"
          :id="`group-${group.id}`"
          :value="group.id"
          v-model="form.groupRolesIds"
        />
        <label class="form-check-label" :for="`group-${group.id}`">{{ group.name }}</label>
      </div>
    </div>
    <div v-else class="text-muted">{{ t('location-visibility-empty') }}</div>

    <!-- Sekcja pasów ruchu -->
    <div class="d-flex align-items-center justify-content-between mt-4">
      <h4 class="mb-0">{{ t('lanes') }}</h4>
      <button type="button" class="btn button-add-user" @click="addLane">+ {{ t('lane-add') }}</button>
    </div>

    <table class="table table-striped align-middle mt-2">
      <thead class="thead-light">
        <tr>
          <th style="width: 20%">{{ t('lane-number') }}</th>
          <th style="width: 35%">{{ t('accuracy-class') }}</th>
          <th style="width: 30%">{{ t('reference-first-axle-avg') }}</th>
          <th style="width: 15%">{{ t('action') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(lane, index) in form.lanes" :key="index">
          <td>
            <input type="number" class="form-control" v-model.number="lane.laneNumber" min="1" required />
          </td>
          <td>
            <select class="form-select" v-model.number="lane.accuracyClassId" required>
              <option :value="null" disabled>{{ t('accuracy-class-select') }}</option>
              <option v-for="ac in accuracyClasses" :key="ac.id" :value="ac.id">{{ ac.name }}</option>
            </select>
          </td>
          <td>
            <input type="number" class="form-control" v-model.number="lane.referenceFirstAxleAvg" min="0" />
          </td>
          <td>
            <button type="button" class="btn button-delete button-compact" @click="removeLane(index)">
              {{ t('delete') }}
            </button>
          </td>
        </tr>
        <tr v-if="!form.lanes.length">
          <td colspan="4" class="text-center text-muted">{{ t('lanes-empty') }}</td>
        </tr>
      </tbody>
    </table>

    <!-- Przyciski akcji -->
    <div class="d-flex gap-2 mt-3">
      <button type="submit" class="btn button-save" :disabled="saving">
        {{ saving ? t('saving') : t('save') }}
      </button>
      <button type="button" class="btn button-add-user" @click="$emit('cancel')">
        {{ t('cancel') }}
      </button>
    </div>
  </form>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import LocationsService from "@/services/LocationsService";

const { t } = useI18n();

const props = defineProps({
  // Id lokalizacji do edycji; null = tryb tworzenia.
  locationId: { type: Number, default: null },
  // Lista klas dokładności (do wyboru przy pasie).
  accuracyClasses: { type: Array, default: () => [] },
});

const emit = defineEmits(["saved", "cancel"]);

const isEdit = ref(props.locationId !== null);
const saving = ref(false);
const errorMessage = ref("");

// Lista grup ról dostępnych do wyboru (widoczność stacji).
const groupRoles = ref([]);

// Domyślny (pusty) stan formularza dla trybu tworzenia.
function emptyForm() {
  return {
    id: null,
    name: "",
    shortName: "",
    cityName: "",
    roadNumber: "",
    organization: "",
    chainage: "",
    direction: "L",
    directionFrom: "",
    directionTo: "",
    geoLat: "",
    geoLong: "",
    checkpointDistance: 1000,
    dataTimeLimit: 13,
    noViolationDataTimeLimit: null,
    imagesLifeLimit: null,
    deleteImages: false,
    ftpReportSubPath: "",
    gddkiaId: null,
    sendDataToGddkia: false,
    sendDataToGddkiaInterval: 60,
    sendLogToGddkia: false,
    cameraPointEnabled: true,
    lanes: [],
    groupRolesIds: [],
  };
}

const form = reactive(emptyForm());

// Mapuje odpowiedź API na stan formularza (tryb edycji).
function hydrateFromApi(data) {
  Object.assign(form, {
    id: data.id,
    name: data.name ?? "",
    shortName: data.shortName ?? "",
    cityName: data.cityName ?? "",
    roadNumber: data.roadNumber ?? "",
    organization: data.organization ?? "",
    chainage: data.chainage ?? "",
    direction: data.direction ?? "L",
    directionFrom: data.directionFrom ?? "",
    directionTo: data.directionTo ?? "",
    geoLat: data.geoLat ?? "",
    geoLong: data.geoLong ?? "",
    checkpointDistance: data.checkpointDistance ?? 1000,
    dataTimeLimit: data.dataTimeLimit ?? 13,
    noViolationDataTimeLimit: data.noViolationDataTimeLimit ?? null,
    imagesLifeLimit: data.imagesLifeLimit ?? null,
    deleteImages: !!data.deleteImages,
    ftpReportSubPath: data.ftpReportSubPath ?? "",
    gddkiaId: data.gddkiaId ?? null,
    sendDataToGddkia: !!data.sendDataToGddkia,
    sendDataToGddkiaInterval: data.sendDataToGddkiaInterval ?? 60,
    sendLogToGddkia: !!data.sendLogToGddkia,
    cameraPointEnabled: !!data.cameraPointEnabled,
    lanes: (data.lanes ?? []).map((l) => ({
      id: l.id,
      laneNumber: l.laneNumber,
      accuracyClassId: l.accuracyClass ? l.accuracyClass.id : null,
      referenceFirstAxleAvg: l.referenceFirstAxleAvg ?? null,
    })),
    groupRolesIds: data.groupRolesIds ?? [],
  });
}

function addLane() {
  // Domyślny numer = kolejny po najwyższym.
  const nextNumber = form.lanes.reduce((max, l) => Math.max(max, l.laneNumber || 0), 0) + 1;
  form.lanes.push({
    id: null,
    laneNumber: nextNumber,
    accuracyClassId: props.accuracyClasses.length ? props.accuracyClasses[0].id : null,
    referenceFirstAxleAvg: null,
  });
}

function removeLane(index) {
  form.lanes.splice(index, 1);
}

// Buduje payload zgodny z LocationUpsertRequest na backendzie.
function buildPayload() {
  return {
    id: form.id,
    name: form.name,
    shortName: form.shortName,
    cityName: form.cityName,
    roadNumber: form.roadNumber,
    organization: form.organization,
    chainage: form.chainage,
    direction: form.direction,
    directionFrom: form.directionFrom,
    directionTo: form.directionTo,
    geoLat: form.geoLat,
    geoLong: form.geoLong,
    checkpointDistance: form.checkpointDistance,
    dataTimeLimit: form.dataTimeLimit,
    noViolationDataTimeLimit: form.noViolationDataTimeLimit,
    imagesLifeLimit: form.imagesLifeLimit,
    deleteImages: form.deleteImages,
    ftpReportSubPath: form.ftpReportSubPath,
    gddkiaId: form.gddkiaId,
    sendDataToGddkia: form.sendDataToGddkia,
    sendDataToGddkiaInterval: form.sendDataToGddkiaInterval,
    sendLogToGddkia: form.sendLogToGddkia,
    cameraPointEnabled: form.cameraPointEnabled,
    lanes: form.lanes.map((l) => ({
      id: l.id ?? null,
      laneNumber: l.laneNumber,
      accuracyClassId: l.accuracyClassId,
      referenceFirstAxleAvg: l.referenceFirstAxleAvg ?? null,
    })),
    groupRolesIds: form.groupRolesIds,
  };
}

async function onSubmit() {
  errorMessage.value = "";

  if (!form.lanes.length) {
    errorMessage.value = t("lanes-required");
    return;
  }

  if (!form.groupRolesIds.length) {
    errorMessage.value = t("location-visibility-required");
    return;
  }

  saving.value = true;
  try {
    const payload = buildPayload();
    if (isEdit.value) {
      await LocationsService.updateLocation(form.id, payload);
    } else {
      await LocationsService.addLocation(payload);
    }
    emit("saved");
  } catch (error) {
    // fetch-wrapper rzuca stringiem "path: userMessage" albo statusem HTTP.
    errorMessage.value = typeof error === "string" ? error : t("location-save-error");
  } finally {
    saving.value = false;
  }
}

onMounted(async () => {
  // Lista grup ról do wyboru widoczności — potrzebna zarówno przy tworzeniu, jak i edycji.
  try {
    groupRoles.value = (await LocationsService.getGroupRoles()) ?? [];
  } catch (error) {
    errorMessage.value = typeof error === "string" ? error : t("location-load-error");
  }

  if (isEdit.value) {
    try {
      const data = await LocationsService.getLocation(props.locationId);
      hydrateFromApi(data);
    } catch (error) {
      errorMessage.value = typeof error === "string" ? error : t("location-load-error");
    }
  }
});
</script>

<style scoped>
.location-form {
  padding: 1rem 1.5rem;
  background-color: white;
}

.location-form h4 {
  margin-top: 0.5rem;
  font-size: 1.05rem;
}

/* Wszystkie przyciski w obrębie zarządzania lokalizacjami zaokrąglone spójnie z resztą projektu. */
.location-form .btn {
  border-radius: 4px !important;
  padding: 0.5rem 1rem;
}

.button-compact {
  padding: 0.25rem 0.6rem;
}

/* Osobny wiersz na checkboxy — równe wyrównanie zamiast „pływania” obok pól o różnej wysokości. */
.checkbox-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 2rem;
  margin-top: 0.75rem;
}

.checkbox-row .form-check {
  margin-bottom: 0;
}

.checkbox-row .form-check .form-check-input {
  border-radius: 0.25rem;
  margin-right: 5px;
}

.checkbox-row .form-check label {
  margin-top: 3px;
}

/* Siatka checkboxów grup ról (widoczność stacji) — zwięzła, wielokolumnowa. */
.group-roles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.35rem 1.5rem;
  margin-top: 0.25rem;
}
</style>
